import React from 'react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ROMAN = ['XII', 'I', 'II', 'III', 'IIII', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI'];

export default function RomanAnalogClock({ time, settings, dark, palette }) {
  const accent = palette?.[0] || '#b45309';
  const h = time.getHours() % 12;
  const m = time.getMinutes();
  const s = time.getSeconds();
  const ms = time.getMilliseconds();
  const secAngle = (s + ms / 1000) * 6;
  const minAngle = (m + s / 60) * 6;
  const hourAngle = (h + m / 60) * 30;

  const ink = dark ? 'rgba(245,230,211,0.9)' : 'rgba(68,47,22,0.95)';
  const faint = dark ? 'rgba(245,230,211,0.25)' : 'rgba(68,47,22,0.25)';
  const face = dark ? 'rgba(28,25,23,0.85)' : 'rgba(254,247,236,0.9)';
  const ring = dark ? 'rgba(245,230,211,0.3)' : 'rgba(68,47,22,0.4)';

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="-120 -120 240 240" className="w-72 h-72 md:w-96 md:h-96">
        <defs>
          <radialGradient id="roman-face" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor={dark ? '#3f3832' : '#fffaf0'} />
            <stop offset="100%" stopColor={dark ? '#1c1917' : '#f5e6d3'} />
          </radialGradient>
        </defs>
        {/* Outer bezel */}
        <circle cx="0" cy="0" r="110" fill="none" stroke={ring} strokeWidth="3" />
        <circle cx="0" cy="0" r="104" fill="url(#roman-face)" />
        {/* Roman numerals */}
        {ROMAN.map((numeral, i) => {
          const a = (i * 30) * Math.PI / 180;
          const x = Math.sin(a) * 85;
          const y = -Math.cos(a) * 85;
          return (
            <text
              key={i}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={ink}
              fontFamily="'Cormorant Garamond', 'DM Serif Display', Georgia, serif"
              fontSize={i % 3 === 0 ? 16 : 14}
              fontWeight={i % 3 === 0 ? 600 : 500}
              style={{ letterSpacing: '0.02em' }}
            >
              {numeral}
            </text>
          );
        })}
        {/* Minute tick marks */}
        {Array.from({ length: 60 }).map((_, i) => {
          if (i % 5 === 0) return null;
          const a = (i * 6) * Math.PI / 180;
          const x1 = Math.sin(a) * 98;
          const y1 = -Math.cos(a) * 98;
          const x2 = Math.sin(a) * 102;
          const y2 = -Math.cos(a) * 102;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={faint} strokeWidth="0.8" />;
        })}
        {/* Hour hand */}
        <line
          x1="0" y1="6"
          x2={Math.sin(hourAngle * Math.PI / 180) * 48}
          y2={-Math.cos(hourAngle * Math.PI / 180) * 48}
          stroke={ink} strokeWidth="5" strokeLinecap="round"
        />
        {/* Minute hand */}
        <line
          x1="0" y1="8"
          x2={Math.sin(minAngle * Math.PI / 180) * 76}
          y2={-Math.cos(minAngle * Math.PI / 180) * 76}
          stroke={ink} strokeWidth="3" strokeLinecap="round"
        />
        {/* Second hand */}
        {settings.showSeconds && (
          <>
            <line
              x1={Math.sin((secAngle + 180) * Math.PI / 180) * 18}
              y1={-Math.cos((secAngle + 180) * Math.PI / 180) * 18}
              x2={Math.sin(secAngle * Math.PI / 180) * 88}
              y2={-Math.cos(secAngle * Math.PI / 180) * 88}
              stroke={accent} strokeWidth="1.5" strokeLinecap="round"
            />
            <circle
              cx={Math.sin(secAngle * Math.PI / 180) * 68}
              cy={-Math.cos(secAngle * Math.PI / 180) * 68}
              r="3" fill="none" stroke={accent} strokeWidth="1"
            />
          </>
        )}
        <circle cx="0" cy="0" r="5" fill={ink} />
        <circle cx="0" cy="0" r="2" fill={face} />
      </svg>
      {settings.showDate && (
        <div className={`text-base font-rasa mt-4 ${dark ? 'text-white/50' : 'text-gray-500/70'}`}>
          {DAYS[time.getDay()]}, {MONTHS[time.getMonth()]} {time.getDate()}
        </div>
      )}
    </div>
  );
}
